// Vision simulation shared by the VisionTool map and the placement optimizer, so the two can never diverge.
import {Bitset, newBitset, setBit} from "./bitset.ts";

export const OBSTACLE = {
    NONE: 0,
    ASTEROID: 1,
    BLOCKING: 2,
};

export type FieldMap = number[][];

export type Location = 'space' | 'ground';

export interface Source {
    uid: string,
    type: 'perception' | 'sensors',
    sensors: number,
    macroBins: boolean,
    x: number,
    y: number,
}

export interface CellResult {
    x: number,
    y: number,
    hasSource: boolean,
    obstacle: number,
    sensors: number
}

/** Highest coordinate of the grid: space maps are 20x20, ground maps 22x22 */
export const gridMax = (location: string) => location === 'ground' ? 21 : 19;

const SENSOR_DIFF = 1;

export const logger = {
    active: false,
    buffer: [] as (string|number)[][],
    log: (...args: (string|number)[]) => {
        if (logger.active) {
            logger.buffer.push([...args]);
        }
    },
    print: (name: string) => {
        if (logger.active) {
            console.groupCollapsed(name);
            for (let i = 0; i < logger.buffer.length; i++) {
                console.log(...logger.buffer[i]);
            }
            console.groupEnd();
        }
        logger.buffer = [];
    },
    clear: () => logger.buffer = []
};

const roundFromReference = (num: number, ref: number) => {
    let diff = num - ref;
    let off = diff < 0 ? Math.round(diff) : -Math.round(-diff);
    return ref + off;
}

const calculatePath = (startX: number, startY: number, targetX: number, targetY: number) => {
    let points = [];
    const startOffset = 1;
    if (startY === targetY) {
        //vertical
        if (startX < targetX) {
            // down
            for (let pX = startX + startOffset; pX <= targetX; pX++) {
                points.push([pX, targetY]);
            }
        } else {
            //up
            for (let pX = startX - startOffset; pX >= targetX; pX--) {
                points.push([pX, targetY]);
            }
        }
    } else if (startX === targetX) {
        // horizontal
        if (startY < targetY) {
            // right
            for (let pY = startY + startOffset; pY <= targetY; pY++) {
                points.push([targetX, pY]);
            }
        } else {
            // left
            for (let pY = startY - startOffset; pY >= targetY; pY--) {
                points.push([targetX, pY]);
            }
        }
    } else {
        // diagonals
        let m = (targetY - startY) / (targetX - startX);
        let q = targetY - m * targetX;

        if (Math.abs(m) > 1) {
            logger.log('|m| > 1');
            if (startY < targetY) {
                for (let pY = startY + startOffset; pY <= targetY; pY++) {
                    let pX = roundFromReference((pY - q) / m, startX);
                    points.push([pX, pY]);
                }
            } else {
                for (let pY = startY - startOffset; pY >= targetY; pY--) {
                    // NOTE: the reference here is startY (not startX), kept as is to preserve the current behaviour
                    let pX = roundFromReference((pY - q) / m, startY);
                    points.push([pX, pY]);
                }
            }
        } else {
            if (startX < targetX) {
                for (let pX = startX + startOffset; pX <= targetX; pX++) {
                    let pY = roundFromReference(m * pX + q, startY);
                    points.push([pX, pY]);
                }
            } else {
                for (let pX = startX - startOffset; pX >= targetX; pX--) {
                    let pY = roundFromReference(m * pX + q, startY);
                    points.push([pX, pY]);
                }
            }
        }
    }
    return points;
}

const getPointSensors = (sourceX: number, sourceY: number, targetX: number, targetY: number, power: number) => {
    let distance = Math.ceil(Math.sqrt(Math.pow(sourceX - targetX, 2) + Math.pow(sourceY - targetY, 2)));
    let raw = power / ((1 + distance));
    return Math.floor(raw);
}

const decreasePower = (power: number) => {
    return power / 2;
}

export const getInitialPower = (source: Pick<Source, 'type' | 'sensors' | 'macroBins'>) => {
    if(source.type === 'perception') {
        let power = source.sensors + 4;
        if(source.macroBins) {
            power += 5;
        }
        return power;
    } else {
        return source.sensors + 1;
    }
}

type SourceLike = Pick<Source, 'type' | 'sensors' | 'macroBins' | 'x' | 'y'>;

/**
 * Casts every ray of a source, in the same order as always, calling `visit` with the sensors value
 * of each in-grid point of each ray. Obstacles along a ray reduce the power for the rest of that ray.
 */
const castRays = (field: FieldMap, s: SourceLike, max: number, visit: (x: number, y: number, p: number) => void) => {
    let range = getInitialPower(s) + SENSOR_DIFF;

    let x1 = s.x - range;
    let x2 = s.x + range;
    let y1 = s.y - range;
    let y2 = s.y + range;

    logger.log('source', s.x, s.y, x1, x2, y1, y2);

    const walk = (path: number[][]) => {
        let power = getInitialPower(s);
        for (let i = 0; i < path.length; i++) {
            let [x, y] = path[i];
            if (x < 0 || x > max || y < 0 || y > max) {
                continue;
            }
            visit(x, y, getPointSensors(s.x, s.y, x, y, power));
            if (field[y][x] === OBSTACLE.BLOCKING) {
                power = 0;
            }
            if (field[y][x] === OBSTACLE.ASTEROID) {
                power = decreasePower(power);
            }
        }
    };

    for (let x = x1; x <= x2; x++) {
        walk(calculatePath(s.x, s.y, x, y1));
        walk(calculatePath(s.x, s.y, x, y2));
    }
    for (let y = y1; y <= y2; y++) {
        walk(calculatePath(s.x, s.y, x1, y));
        walk(calculatePath(s.x, s.y, x2, y));
    }
};

/** Sensors value of every cell of the map given all sources */
export const simulate = (field: FieldMap, sources: Source[], location: string): CellResult[][] => {
    const MAX = gridMax(location);
    let temp: CellResult[][] = [];
    for (let y = 0; y <= MAX; y++) {
        temp[y] = [];
        for (let x = 0; x <= MAX; x++) {
            let hasSource = sources.find(el => el.x === x && el.y === y);

            temp[y][x] = {x, y, hasSource: !!hasSource, obstacle: field[y] ? field[y][x] : OBSTACLE.NONE, sensors: 0};
        }
    }

    sources.forEach(s => {
        temp[s.y][s.x].sensors += getInitialPower(s);

        // each cell gets the first non-zero value a ray gives it, other rays of the same source don't add up
        let inner: FieldMap = [];
        castRays(field, s, MAX, (x, y, p) => {
            if (!inner[y]) {
                inner[y] = [];
            }
            logger.log(x, y, inner[y][x] ?? null, p);
            if (!inner[y][x]) {
                inner[y][x] = p;
                temp[y][x].sensors += p;
            } else if (inner[y][x] !== p) {
                inner[y][x] = Math.max(inner[y][x], p);
                logger.log('conflict ', x, y, inner[y][x], p);
            }
        });

        logger.print(s.x + ' ' + s.y);
    });

    return temp;
}

/**
 * Cells (index y * (max + 1) + x) that a single source makes visible, i.e. given a non-zero sensors value.
 * Since a source's contribution never depends on other sources, a cell is visible on the map iff
 * at least one source's coverage contains it.
 */
export const coverageOf = (field: FieldMap, source: SourceLike, max: number): Bitset => {
    const size = max + 1;
    const cov = newBitset(size * size);
    if (getInitialPower(source) > 0) {
        setBit(cov, source.y * size + source.x);
    }
    castRays(field, source, max, (x, y, p) => {
        if (p > 0) {
            setBit(cov, y * size + x);
        }
    });
    logger.clear();
    return cov;
}
